// =============================================================================
// ClientManagement
//
// Admin panel for viewing clients and triggering per-client admin actions.
// Modals gate all destructive or significant actions behind a confirmation
// step.
// =============================================================================

import { FunctionComponent, useEffect, useState } from "react";
import {
  CLIENT_MANAGEMENT,
  TRIGGER_MAP_MODAL,
  RE_ONBOARD_MODAL,
  REPAIR_MODAL,
} from "src/constants/adminDashboard";
import {
  fetchAllClients,
  triggerClientMap,
  reOnboardClient,
  repairClient,
} from "src/utils/admin/clients";
import { Client } from "src/utils/types/UserTypes";
import ClientRow from "./ClientRow";
import TriggerMapModal from "./TriggerMapModal";
import ReOnboardModal from "./ReOnboardModal";
import RepairModal from "./RepairModal";

const ClientManagement: FunctionComponent = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [mapTarget, setMapTarget] = useState<Client | null>(null);
  const [reOnboardTarget, setReOnboardTarget] = useState<Client | null>(null);
  const [repairTarget, setRepairTarget] = useState<Client | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const data = await fetchAllClients();
        setClients(data);
      } catch {
        setError(CLIENT_MANAGEMENT.error);
      } finally {
        setLoading(false);
      }
    };
    loadClients();
  }, []);

  const filteredClients = clients.filter(
    (c) =>
      c.userInfo.name.toLowerCase().includes(search.toLowerCase()) ||
      c.userInfo.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleTriggerMap = async () => {
    if (!mapTarget) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await triggerClientMap(mapTarget.userId);
      setActionSuccess(TRIGGER_MAP_MODAL.success);
      setMapTarget(null);
    } catch {
      setActionError(TRIGGER_MAP_MODAL.error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReOnboard = async () => {
    if (!reOnboardTarget) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await reOnboardClient(reOnboardTarget.userId);
      setActionSuccess(RE_ONBOARD_MODAL.success);
      setReOnboardTarget(null);
    } catch {
      setActionError(RE_ONBOARD_MODAL.error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRepair = async (ssn: string) => {
    if (!repairTarget) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const { userInfo } = repairTarget;
      const nameParts = userInfo.name.trim().split(/\s+/);
      await repairClient({
        uid: repairTarget.userId,
        name: userInfo.name,
        email: userInfo.email,
        ssn,
        firstName: nameParts[0] || "",
        lastName: nameParts[nameParts.length - 1] || "",
        addressLineOne: userInfo.address,
        city: userInfo.city,
        state: userInfo.state,
        zip: userInfo.zip,
        dateOfBirth: userInfo.dob,
      });
      setActionSuccess(REPAIR_MODAL.success);
      setRepairTarget(null);
    } catch {
      setActionError(REPAIR_MODAL.error);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        {CLIENT_MANAGEMENT.title}
      </h2>
      <p className="text-sm text-gray-500 mb-6">{CLIENT_MANAGEMENT.subtitle}</p>

      {actionSuccess && (
        <p className="text-green-600 text-sm mb-4">{actionSuccess}</p>
      )}
      {actionError && (
        <p className="text-red-600 text-sm mb-4">{actionError}</p>
      )}

      <input
        type="text"
        placeholder={CLIENT_MANAGEMENT.search.placeholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="border border-gray-300 rounded px-3 py-2 text-sm mb-4 w-full max-w-sm"
      />

      {loading && <p className="text-sm text-gray-400">Loading...</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && (
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase">
                Name
              </th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase">
                Email
              </th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase">
                User ID
              </th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-4 px-4 text-sm text-gray-400">
                  {CLIENT_MANAGEMENT.empty}
                </td>
              </tr>
            ) : (
              filteredClients.map((client) => (
                <ClientRow
                  key={client.userId}
                  client={client}
                  onTriggerMap={setMapTarget}
                  onReOnboard={setReOnboardTarget}
                  onRepair={setRepairTarget}
                />
              ))
            )}
          </tbody>
        </table>
      )}

      {mapTarget && (
        <TriggerMapModal
          clientName={mapTarget.userInfo.name}
          onConfirm={handleTriggerMap}
          onCancel={() => setMapTarget(null)}
          loading={actionLoading}
        />
      )}

      {reOnboardTarget && (
        <ReOnboardModal
          clientName={reOnboardTarget.userInfo.name}
          onConfirm={handleReOnboard}
          onCancel={() => setReOnboardTarget(null)}
          loading={actionLoading}
        />
      )}

      {repairTarget && (
        <RepairModal
          clientName={repairTarget.userInfo.name}
          onConfirm={handleRepair}
          onCancel={() => setRepairTarget(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default ClientManagement;

